import React, { useCallback, useEffect, useState } from 'react';
import { DndContext, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { useArchitectureState } from './hooks/useArchitectureState';
import { useArchitecturePersistence } from './hooks/useArchitecturePersistence';
import { useArchitectureVendorOverlay } from './hooks/useArchitectureVendorOverlay';
import ArchitectureCanvas from './ArchitectureCanvas';
import ArchitectureSidebar from './ArchitectureSidebar';
import ArchitectureLegend from './ArchitectureLegend';
import ArchitectureVendorPicker from './ArchitectureVendorPicker';
import ViewModeToggle from './ViewModeToggle';
import TechnologySelectorModal from './TechnologySelectorModal';
import './ArchitectureDiagram.css';

/**
 * Maps diagram node categories to CISA function codes
 * This enables sync between the architecture diagram and CISA assessment
 */
const CATEGORY_TO_FUNCTION = {
  // IDENTITY pillar
  idp: 'ID_AUTH',
  mfa: 'ID_AUTH',
  access: 'ID_ACCESS',
  pam: 'ID_ACCESS',
  directory: 'ID_STORES',
  analytics: 'ID_RISK',
  // DEVICES pillar
  mdm: 'DEV_POLICY',
  compliance: 'DEV_POLICY',
  edr: 'DEV_THREAT',
  asset: 'DEV_ASSET',
  ztna: 'DEV_ACCESS',
  // NETWORKS pillar
  segmentation: 'NET_SEG',
  ndr: 'NET_TRAFFIC',
  microseg: 'NET_SEG',
  encryption: 'NET_ENCRYPT',
  resilience: 'NET_RESIL',
  // APPLICATIONS pillar
  waf: 'APP_THREAT',
  rasp: 'APP_THREAT',
  devsec: 'APP_SECDEV',
  // DATA pillar
  discovery: 'DATA_INV',
  classification: 'DATA_CAT',
  dlp: 'DATA_ACCESS',
  backup: 'DATA_AVAIL',
  // Custom/fallback
  custom: null
};

/**
 * Get CISA function code from a node's category
 */
function getFunctionCodeFromCategory(category) {
  return CATEGORY_TO_FUNCTION[category] || null;
}

/**
 * ArchitectureDiagram Component
 *
 * Main container for the CISA Zero Trust Architecture Diagram.
 * Provides an interactive visualization of technology components
 * with draggable nodes and connection lines.
 */
function ArchitectureDiagram({
  selectedDepartment,
  productAdoptions = {},
  onAdoptProduct,
  onRemoveProduct
}) {
  const departmentId = selectedDepartment?.department_id;

  // State management
  const {
    selectedPillar,
    switchPillar,
    nodes,
    addNode,
    moveNode,
    updateNode,
    deleteNode,
    undoDelete,
    canUndo,
    getNodeById,
    edges,
    addEdge,
    updateEdge,
    deleteEdge,
    selectedNodeId,
    setSelectedNodeId,
    selectedEdgeId,
    setSelectedEdgeId,
    viewport,
    pan,
    zoom,
    resetViewport,
    resetPillar,
    getState,
    loadState
  } = useArchitectureState('IDENTITY', productAdoptions);

  // Persistence
  const { debouncedSave, load } = useArchitecturePersistence(
    departmentId,
    getState,
    loadState
  );

  // Technology selector modal state
  const [selectorModalNode, setSelectorModalNode] = useState(null);

  // Vendor overlay (highlight feature)
  const {
    selectedVendorNames,
    toggleVendor,
    clearVendors,
    isNodeHighlighted,
    getNodeCoverage,
    getHighlightColor,
    getVendorLogo,
    getVendorProduct,
    hasOverlay: hasVendorOverlay,
    viewMode,
    setViewMode
  } = useArchitectureVendorOverlay();

  // Save state on changes
  useEffect(() => {
    debouncedSave();
  }, [nodes, edges, selectedPillar, viewport, debouncedSave]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    })
  );

  /**
   * Handle drag end for nodes and palette items
   */
  const handleDragEnd = useCallback((event) => {
    const { active, delta } = event;

    if (!active) return;

    const { type, node, product, pillarCode } = active.data.current || {};

    // Handle node drag (reposition)
    if (type === 'node' && node) {
      const newX = Math.max(0, Math.min(800, node.x + delta.x));
      const newY = Math.max(0, Math.min(450, node.y + delta.y));
      moveNode(node.id, newX, newY);
      return;
    }

    // Handle palette product drag (add new node)
    if (type === 'palette-product' && product) {
      // Calculate drop position based on delta from palette
      // Default to center of canvas if delta is small
      const dropX = Math.abs(delta.x) > 50 ? 300 + delta.x : 400;
      const dropY = Math.abs(delta.y) > 50 ? 200 + delta.y : 250;

      addNode({
        label: product.name,
        vendorName: product.name,
        productName: product.product,
        category: product.category,
        icon: getCategoryIcon(product.category),
        x: Math.max(60, Math.min(700, dropX)),
        y: Math.max(40, Math.min(400, dropY))
      });

      // Sync with CISA assessment
      const functionCode = getFunctionCodeFromCategory(product.category);
      if (functionCode && onAdoptProduct && product.product) {
        onAdoptProduct(functionCode, product.product, 'IN_USE');
      }
    }
  }, [moveNode, addNode, onAdoptProduct]);

  /**
   * Get icon for a category
   */
  const getCategoryIcon = (category) => {
    const iconMap = {
      idp: 'key',
      mfa: 'shield-check',
      access: 'user-check',
      pam: 'shield',
      directory: 'database',
      analytics: 'activity',
      mdm: 'settings',
      compliance: 'check-circle',
      edr: 'shield-alert',
      asset: 'package',
      ztna: 'lock',
      segmentation: 'git-branch',
      ndr: 'activity',
      microseg: 'grid',
      encryption: 'lock',
      resilience: 'shield',
      waf: 'shield',
      rasp: 'shield-alert',
      devsec: 'search',
      discovery: 'search',
      classification: 'tag',
      dlp: 'eye-off',
      backup: 'archive',
      custom: 'layers'
    };
    return iconMap[category] || 'layers';
  };

  /**
   * Handle adding a custom node from sidebar
   * Also syncs with CISA assessment by calling onAdoptProduct
   */
  const handleAddNode = useCallback((nodeData) => {
    addNode({
      ...nodeData,
      icon: getCategoryIcon(nodeData.category)
    });

    // Sync with CISA assessment
    const functionCode = getFunctionCodeFromCategory(nodeData.category);
    if (functionCode && onAdoptProduct && nodeData.productName) {
      onAdoptProduct(functionCode, nodeData.productName, 'IN_USE');
    }
  }, [addNode, onAdoptProduct]);

  /**
   * Handle deleting a node and syncing with CISA assessment
   */
  const handleDeleteNode = useCallback((nodeId) => {
    // Find the node before deleting to get product info
    const node = getNodeById(nodeId);

    // Delete the node from the diagram
    deleteNode(nodeId);

    // Sync with CISA assessment - remove the product
    if (node && onRemoveProduct && node.productName) {
      const functionCode = getFunctionCodeFromCategory(node.category);
      if (functionCode) {
        onRemoveProduct(functionCode, node.productName);
      }
    }
  }, [deleteNode, getNodeById, onRemoveProduct]);

  /**
   * Handle opening the technology selector for a node
   */
  const handleOpenSelector = useCallback((node) => {
    setSelectorModalNode(node);
  }, []);

  /**
   * Handle technology selection from the modal
   * Syncs changes with CISA assessment
   */
  const handleSelectTechnology = useCallback((techData) => {
    if (selectorModalNode) {
      // Get old product info before updating
      const oldNode = selectorModalNode;
      const oldFunctionCode = getFunctionCodeFromCategory(oldNode.category);
      const newFunctionCode = getFunctionCodeFromCategory(techData.category);

      // Update the node
      updateNode(selectorModalNode.id, {
        label: techData.label,
        vendorName: techData.vendorName,
        productName: techData.productName,
        category: techData.category,
        icon: techData.icon
      });

      // Sync with CISA assessment
      // Remove old product if it exists and is different
      if (oldNode.productName && onRemoveProduct && oldFunctionCode) {
        if (oldNode.productName !== techData.productName || oldFunctionCode !== newFunctionCode) {
          onRemoveProduct(oldFunctionCode, oldNode.productName);
        }
      }

      // Add new product
      if (techData.productName && onAdoptProduct && newFunctionCode) {
        onAdoptProduct(newFunctionCode, techData.productName, 'IN_USE');
      }
    }
    setSelectorModalNode(null);
  }, [selectorModalNode, updateNode, onAdoptProduct, onRemoveProduct]);

  // Detect mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
    >
      <div className="arch-diagram">
        {/* Sidebar with pillar tabs and product palette */}
        <ArchitectureSidebar
          selectedPillar={selectedPillar}
          onSelectPillar={switchPillar}
          onResetPillar={resetPillar}
          onAddNode={handleAddNode}
          onUndo={undoDelete}
          canUndo={canUndo}
          existingNodes={nodes}
          readOnly={false}
        />

        {/* Main canvas area */}
        <div className="arch-diagram__main">
          {/* Toolbar with pillar title and vendor picker */}
          <div className="arch-diagram__toolbar">
            <span className="arch-diagram__toolbar-title">
              {selectedPillar.charAt(0) + selectedPillar.slice(1).toLowerCase()} Architecture
            </span>
            <div className="arch-diagram__toolbar-actions">
              <ArchitectureVendorPicker
                selectedVendors={selectedVendorNames}
                onToggle={toggleVendor}
                onClear={clearVendors}
              />
              <ViewModeToggle
                viewMode={viewMode}
                onSetViewMode={setViewMode}
                disabled={!hasVendorOverlay}
              />
            </div>
          </div>

          <ArchitectureCanvas
            nodes={nodes}
            edges={edges}
            viewport={viewport}
            selectedNodeId={selectedNodeId}
            selectedEdgeId={selectedEdgeId}
            onSelectNode={setSelectedNodeId}
            onSelectEdge={setSelectedEdgeId}
            onDeleteNode={handleDeleteNode}
            onDeleteEdge={deleteEdge}
            onAddEdge={addEdge}
            onOpenSelector={handleOpenSelector}
            onPan={pan}
            onZoom={zoom}
            getNodeById={getNodeById}
            readOnly={isMobile}
            hasVendorOverlay={hasVendorOverlay}
            isNodeHighlighted={isNodeHighlighted}
            getNodeCoverage={getNodeCoverage}
            getHighlightColor={getHighlightColor}
            getVendorLogo={getVendorLogo}
            getVendorProduct={getVendorProduct}
          />

          {/* Legend */}
          <ArchitectureLegend />

          {/* Mobile notice */}
          {isMobile && (
            <div className="arch-diagram__mobile-notice">
              View only on mobile. Use desktop for editing.
            </div>
          )}
        </div>
      </div>

      {/* Drag overlay for visual feedback */}
      <DragOverlay>
        {/* Optionally render a preview of dragged item here */}
      </DragOverlay>

      {/* Technology Selector Modal */}
      <TechnologySelectorModal
        isOpen={!!selectorModalNode}
        onClose={() => setSelectorModalNode(null)}
        node={selectorModalNode}
        pillarCode={selectedPillar}
        onSelectTechnology={handleSelectTechnology}
      />
    </DndContext>
  );
}

export default ArchitectureDiagram;
